import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUpsertActivite } from '@/hooks/queries/useActivites';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useUsersList } from '@/hooks/queries/useUsersList';
import type { ActiviteRow, CanalActivite } from '@/lib/database.types';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  activite?: ActiviteRow | null;
}

const CANAUX: { code: CanalActivite; label: string }[] = [
  { code: 'B2B', label: 'B2B' },
  { code: 'B2C', label: 'B2C' },
  { code: 'Mixte', label: 'Mixte' },
];

export function ActiviteFormDialog({ open, onOpenChange, activite }: Props): JSX.Element {
  const isEdit = !!activite;
  const upsert = useUpsertActivite();
  const { data: entreprises = [] } = useEntreprises();
  const { data: users = [] } = useUsersList();

  const [entrepriseId, setEntrepriseId] = useState('');
  const [code, setCode] = useState('');
  const [nom, setNom] = useState('');
  const [canal, setCanal] = useState<CanalActivite>('B2B');
  const [description, setDescription] = useState('');
  const [responsableId, setResponsableId] = useState('');
  const [ordre, setOrdre] = useState<number>(0);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (activite) {
      setEntrepriseId(activite.entreprise_id);
      setCode(activite.code);
      setNom(activite.nom);
      setCanal(activite.canal);
      setDescription(activite.description ?? '');
      setResponsableId(activite.responsable_id ?? '');
      setOrdre(activite.ordre ?? 0);
      setIsActive(activite.is_active);
    } else {
      setEntrepriseId('');
      setCode('');
      setNom('');
      setCanal('B2B');
      setDescription('');
      setResponsableId('');
      setOrdre(0);
      setIsActive(true);
    }
  }, [open, activite]);

  const submit = async (): Promise<void> => {
    if (!entrepriseId) return toast.error('Entreprise obligatoire') as never;
    if (!code.trim()) return toast.error('Code obligatoire') as never;
    if (!nom.trim()) return toast.error('Nom obligatoire') as never;
    try {
      await upsert.mutateAsync({
        ...(isEdit && activite ? { id: activite.id } : {}),
        entreprise_id: entrepriseId,
        code: code.trim(),
        nom: nom.trim(),
        canal,
        description: description.trim() || null,
        responsable_id: responsableId || null,
        ordre,
        is_active: isActive,
      });
      toast.success(isEdit ? 'Activité modifiée' : 'Activité créée');
      onOpenChange(false);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier l’activité' : 'Nouvelle activité'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Entreprise" required>
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="input"
              required
            >
              <option value="">— Sélectionner —</option>
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Code (slug)" required hint="Ex. trip_sun, formation_b2b">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="input font-mono"
                placeholder="formation_b2b"
                required
              />
            </Field>
            <Field label="Nom affiché" required>
              <input
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="input"
                placeholder="Formation B2B"
                required
              />
            </Field>
          </div>
          <Field label="Canal commercial">
            <div className="grid grid-cols-3 gap-2">
              {CANAUX.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => setCanal(c.code)}
                  className={cn(
                    'rounded-lg border-2 px-3 py-2 text-xs font-semibold transition-all',
                    canal === c.code
                      ? 'border-zinc-700 bg-zinc-100 text-zinc-900'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Responsable">
              <select
                value={responsableId}
                onChange={(e) => setResponsableId(e.target.value)}
                className="input"
              >
                <option value="">— Aucun —</option>
                {users.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.full_name ?? u.user_id.slice(0, 8)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ordre d’affichage" hint="Plus petit = plus haut">
              <input
                type="number"
                value={ordre}
                onChange={(e) => setOrdre(Number(e.target.value))}
                className="input tabular-nums"
                min={0}
              />
            </Field>
          </div>
          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input min-h-[60px]"
              rows={2}
            />
          </Field>
          <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4"
            />
            <span>
              <strong>Active</strong> — les activités inactives restent en DB pour traçabilité mais
              ne sont plus proposées dans les sélecteurs.
            </span>
          </label>
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={upsert.isPending}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-zinc-800 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-zinc-900',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {upsert.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {isEdit ? 'Enregistrer' : 'Créer l’activité'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
      {hint && <span className="mt-0.5 block text-[10px] text-slate-400">{hint}</span>}
    </label>
  );
}
